import { type FC, useEffect, useMemo, useState } from "react"

import Button from "~/components/Button"
import InputField from "~/components/InputField"
import RichContentEditor from "~/components/RichContentEditor"
import { exportRichContent, parseRichContent, type RichContentValue } from "~/utilities/richContent"

import Attachments from "./Attachments"
import Links from "./Links"
import { ButtonsGroup, ButtonsRow } from "./styles"

type Props = {
	page: {
		id: number
		title: string
		text: string | null
		attachments: { id: number; fileType: string; label: string; url: string }[]
		links: { id: number; label: string; url: string }[]
	}
	isSaving: boolean
	isDeleting: boolean
	onClose(): void
	onSavePage(data: { title: string; text: string | null }): void
	onDeletePage(): void
}

const CustomContent: FC<Props> = ({ page, isSaving, isDeleting, onClose, onSavePage, onDeletePage }) => {
	const initialContent = useMemo(() => parseRichContent(page.text), [page.text])

	const [title, setTitle] = useState(page.title)
	const [text, setText] = useState<RichContentValue | null>(
		initialContent === null ? null : { isModified: false, isEmpty: false, value: initialContent },
	)

	useEffect(() => {
		setTitle(page.title)
		setText(initialContent === null ? null : { isModified: false, isEmpty: false, value: initialContent })
	}, [page.title, initialContent])

	return (
		<>
			<InputField label="Title" value={title} onChange={setTitle} />
			<RichContentEditor
				placeholder="Enter page content here..."
				initialValue={initialContent}
				setValue={setText}
			/>
			<Attachments pageId={page.id} attachments={page.attachments} />
			<Links pageId={page.id} links={page.links} />
			<ButtonsRow>
				<ButtonsGroup>
					<Button variant="secondary" onClick={onClose} isDisabled={isSaving || isDeleting}>
						Cancel
					</Button>
					<Button
						variant="primary"
						isDisabled={title.trim().length === 0 || isDeleting}
						isLoading={isSaving}
						onClick={() => onSavePage({ title, text: exportRichContent(text) })}
					>
						Save
					</Button>
				</ButtonsGroup>
				<Button variant="primary-danger" onClick={onDeletePage} isDisabled={isSaving} isLoading={isDeleting}>
					Delete
				</Button>
			</ButtonsRow>
		</>
	)
}

export default CustomContent
